import React from 'react';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import VerifiedIcon from '@mui/icons-material/Verified';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Avatar, Box, Chip, Grid, Link, Typography } from '@mui/material';

// Detail dialog for a Creator Marketplace (Meta) creator. Mirrors the InsightFrame BootstrapDialog
// shell used by the existing "Show Details" flow, but renders the full Meta panel (insights +
// per-media + audience breakdown) instead of the internal IQ iframe (Meta creators aren't in the DB).

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': { padding: theme.spacing(2) },
  '& .MuiDialogActions-root': { padding: theme.spacing(1) },
}));

const fmt = (n) => {
  if (n === null || n === undefined || n === '') return '—';
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return String(num);
};
const pct = (n) => (n === null || n === undefined || n === '' ? '—' : `${n}%`);

function Metric({ label, value }) {
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, textAlign: 'center' }}>
      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{label}</Typography>
      <Typography sx={{ fontSize: 17, fontWeight: 800 }}>{value}</Typography>
    </Box>
  );
}

function Bars({ title, rows }) {
  if (!rows || !rows.length) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', mb: 1 }}>{title}</Typography>
      {rows.map((r) => (
        <Box key={r.label} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography sx={{ fontSize: 12, width: 72, flexShrink: 0 }}>{r.label}</Typography>
          <Box sx={{ flex: 1, background: '#f0f0f0', borderRadius: 1, height: 10 }}>
            <Box sx={{ width: `${r.pct}%`, background: 'secondary.main', bgcolor: 'secondary.main', height: 10, borderRadius: 1 }} />
          </Box>
          <Typography sx={{ fontSize: 12, width: 36, textAlign: 'right' }}>{r.pct}%</Typography>
        </Box>
      ))}
    </Box>
  );
}

function MediaCard({ m }) {
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
      <Box
        sx={{
          height: 90,
          background: m.thumbnail_url
            ? `center / cover no-repeat url(${m.thumbnail_url})`
            : 'linear-gradient(135deg,#e5e7eb,#cbd5e1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {!m.thumbnail_url && (
          <Typography sx={{ fontSize: 11, color: '#64748b' }}>{m.media_type || 'MEDIA'}</Typography>
        )}
      </Box>
      <Box sx={{ p: 1 }}>
        <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
          {m.product_type && <Chip label={m.product_type} size="small" />}
          {m.tagged_brand && (
            <Chip label={m.tagged_brand} size="small" color="secondary" variant="outlined" />
          )}
        </Box>
        {m.caption && (
          <Typography
            sx={{
              fontSize: 11,
              color: 'text.secondary',
              mb: 0.5,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {m.caption}
          </Typography>
        )}
        <Typography sx={{ fontSize: 11 }}>
          ♥ {fmt(m.likes)} · 💬 {fmt(m.comments)}
          {m.views ? ` · ▶ ${fmt(m.views)}` : ''}
          {m.shares ? ` · ↗ ${fmt(m.shares)}` : ''}
        </Typography>
        <Link href={m.permalink} target="_blank" rel="noreferrer" sx={{ fontSize: 11 }}>
          open <OpenInNewIcon sx={{ fontSize: 10, verticalAlign: 'middle' }} />
        </Link>
      </Box>
    </Box>
  );
}

function MediaGrid({ title, items }) {
  if (!items || !items.length) return null;
  return (
    <>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', mb: 1 }}>{title}</Typography>
      <Grid container spacing={1} sx={{ mb: 2 }}>
        {items.map((m) => (
          <Grid item xs={6} sm={3} key={m.id}>
            <MediaCard m={m} />
          </Grid>
        ))}
      </Grid>
    </>
  );
}

export default function CreatorMarketplaceDetail({ creator, open, onClose }) {
  if (!creator) return null;
  const c = creator;
  const meta = [c.country, c.gender, c.age_bucket].filter(Boolean).join(' · ') || '—';
  const a = c.audience || {};
  const media = c.recent_media || [];

  return (
    <BootstrapDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, pr: 6 }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Avatar src={c.profile_picture_url} sx={{ width: 48, height: 48 }}>
            {(c.name || '?').slice(0, 1)}
          </Avatar>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontWeight: 800, fontSize: 18 }}>{c.name}</Typography>
              {c.is_account_verified && <VerifiedIcon sx={{ fontSize: 18, color: 'secondary.main' }} />}
            </Box>
            <Link href={c.instagram_url} target="_blank" rel="noreferrer" sx={{ fontSize: 13 }}>
              @{c.username} <OpenInNewIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} />
            </Link>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{meta}</Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {c.biography && <Typography sx={{ fontSize: 13, mb: 1.5 }}>{c.biography}</Typography>}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, alignItems: 'center' }}>
          {c.onboarded_status && (
            <Chip
              size="small"
              color={c.onboarded_status === 'onboarded' ? 'success' : 'default'}
              label={`Marketplace: ${c.onboarded_status}`}
            />
          )}
          {c.email && (
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>✉ {c.email}</Typography>
          )}
          {c.portfolio_url && (
            <Link href={c.portfolio_url} target="_blank" rel="noreferrer" sx={{ fontSize: 12 }}>
              Portfolio <OpenInNewIcon sx={{ fontSize: 11, verticalAlign: 'middle' }} />
            </Link>
          )}
        </Box>

        {/* Insights */}
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', mb: 1 }}>INSIGHTS</Typography>
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={4}><Metric label="Followers" value={fmt(c.total_followers)} /></Grid>
          <Grid item xs={6} sm={4}><Metric label="Engaged accounts" value={fmt(c.creator_engaged_accounts)} /></Grid>
          <Grid item xs={6} sm={4}><Metric label="Reach" value={fmt(c.creator_reach)} /></Grid>
          <Grid item xs={6} sm={4}><Metric label="Reels interaction" value={pct(c.reels_interaction_rate)} /></Grid>
          <Grid item xs={6} sm={4}><Metric label="Reels hook rate" value={pct(c.reels_hook_rate)} /></Grid>
        </Grid>

        {/* Audience breakdown */}
        {(a.age?.length || a.gender?.length || a.top_countries?.length || a.top_cities?.length) && (
          <>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', mb: 1 }}>AUDIENCE</Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}><Bars title="Age" rows={a.age} /><Bars title="Gender" rows={a.gender} /></Grid>
              <Grid item xs={12} sm={6}><Bars title="Top countries" rows={a.top_countries} /><Bars title="Top cities" rows={a.top_cities} /></Grid>
            </Grid>
          </>
        )}

        {/* Media: recent, branded content, partnership ads */}
        <MediaGrid title="RECENT MEDIA" items={media} />
        <MediaGrid title="BRANDED CONTENT" items={c.branded_content_media} />
        <MediaGrid title="PARTNERSHIP ADS" items={c.past_partnership_ads_media} />

        {/* Partnership */}
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', mt: 1, mb: 0.5 }}>
          BRAND PARTNERSHIPS
        </Typography>
        <Typography sx={{ fontSize: 13, mb: 0.5 }}>
          Experience:{' '}
          <b style={{ color: c.has_brand_partnership_experience ? '#2e7d32' : '#9e9e9e' }}>
            {c.has_brand_partnership_experience ? 'Yes' : 'No'}
          </b>
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {(c.past_brand_partnership_partners || []).map((p) => (
            <Chip key={p} label={p} size="small" variant="outlined" />
          ))}
        </Box>
      </DialogContent>
    </BootstrapDialog>
  );
}
